import './autopilot-form.scss';

import {
	Checkbox,
	MenuItem,
	OutlinedInput,
	type SelectChangeEvent,
} from '@mui/material';
import {
	selectCurrentSeason,
	unsavedDataChanged,
} from 'features/rules/redux/rules-slice';
import { ErrorMessage, Field, useFormikContext } from 'formik';
import { useAppDispatch, useAppSelector } from 'redux/hooks';

import { NumberInput } from 'components/number-input/number-input';
import { useUser } from 'features/users/context/userContext';
import { Select } from 'formik-mui';
import { AutopilotOption } from 'graphql/gql-types';
import { useAppTracking } from 'hooks/useTracking';
import { labels } from 'locales/en.label';
import { useEffect } from 'react';
import { SeasonSelect } from '../season-select/season-select';
import type { AutopilotFormValues } from './autopilot';

const commonLabels = labels.common;
const autopilotLabels = labels.rules.autopilot_tab;

const AutopilotSelect = ({ name }: { name: string }) => {
	const { isReadOnly } = useUser();
	const selectedSeason = useAppSelector(selectCurrentSeason);
	const { track } = useAppTracking();

	const priority = name?.split('autopilot')[1];

	const handleChange = (e: SelectChangeEvent<string>) => {
		// options Y and P (positive only) are considered TRUE for enabled.
		const enabled = e.target.value !== AutopilotOption.N;

		track({
			action: 'Autopilot Select',
			page: 'Rules',
			autopilotPriority: priority,
			autopilotEnabled: enabled,
			autopilotOption: e.target.value,
			seasonName: selectedSeason?.season_name,
			seasonId: selectedSeason?.season_id,
		});
	};

	return (
		<div className='common-dropdown-container autopilot-select-container'>
			<Field
				name={name}
				component={Select}
				onChange={handleChange}
				disabled={isReadOnly}
			>
				<MenuItem value={AutopilotOption.Y}>{commonLabels.yes}</MenuItem>
				<MenuItem value={AutopilotOption.N}>{commonLabels.no}</MenuItem>
				<MenuItem value={AutopilotOption.P}>
					{autopilotLabels.positive_only}
				</MenuItem>
			</Field>
		</div>
	);
};

const IntradayAutopilotCheckbox = ({
	name,
	regularAutopilot,
	includeIntraday,
}: {
	name: 'includeIntradayHigh' | 'includeIntradayMed' | 'includeIntradayLow';
	regularAutopilot: AutopilotOption;
	includeIntraday: boolean;
}) => {
	const { isReadOnly } = useUser();
	const selectedSeason = useAppSelector(selectCurrentSeason);
	const { setFieldValue } = useFormikContext<AutopilotFormValues>();
	const { track } = useAppTracking();

	const priority = name.replace('includeIntraday', '');
	const disabled = isReadOnly || regularAutopilot === AutopilotOption.N;
	const checked = regularAutopilot !== AutopilotOption.N && includeIntraday;

	return (
		<div className='intraday-checkbox-container'>
			<Checkbox
				disableRipple
				size='small'
				disabled={disabled}
				checked={checked}
				onChange={(event) => {
					const nextValue = event.target.checked;
					setFieldValue(name, nextValue);
					track({
						action: 'Autopilot Intraday Checkbox',
						page: 'Rules',
						autopilotPriority: priority,
						autopilotEnabled: nextValue,
						autopilotOption:
							nextValue && regularAutopilot === AutopilotOption.P
								? AutopilotOption.P
								: nextValue
									? AutopilotOption.Y
									: AutopilotOption.N,
						seasonName: selectedSeason?.season_name,
						seasonId: selectedSeason?.season_id,
					});
				}}
			/>
		</div>
	);
};

export const AutopilotForm = () => {
	const { isReadOnly } = useUser();
	const dispatch = useAppDispatch();
	const { values, dirty, handleChange, setFieldValue } =
		useFormikContext<AutopilotFormValues>();

	useEffect(() => {
		dispatch(unsavedDataChanged(dirty));
	}, [dirty, dispatch]);

	useEffect(() => {
		if (
			values.autopilotHigh === AutopilotOption.N &&
			values.includeIntradayHigh
		) {
			setFieldValue('includeIntradayHigh', false, false);
		}
		if (
			values.autopilotMed === AutopilotOption.N &&
			values.includeIntradayMed
		) {
			setFieldValue('includeIntradayMed', false, false);
		}
		if (
			values.autopilotLow === AutopilotOption.N &&
			values.includeIntradayLow
		) {
			setFieldValue('includeIntradayLow', false, false);
		}
	}, [
		setFieldValue,
		values.autopilotHigh,
		values.autopilotLow,
		values.autopilotMed,
		values.includeIntradayHigh,
		values.includeIntradayLow,
		values.includeIntradayMed,
	]);

	return (
		<div className='autopilot-form'>
			<div className='row'>
				<div className='common-dropdown-container season-name'>
					<SeasonSelect />
				</div>
			</div>

			<div className='row ap-error'>
				<ErrorMessage name='priorityThresholdHigh' />
			</div>

			<div className='header'>
				<span>{commonLabels.priority}</span>
				<div className='header-columns'>
					<span>{labels.rules.autopilot}</span>
					<span>{autopilotLabels.intraday}</span>
				</div>
			</div>

			<div className='top-labels'>
				<span className='label large' />
				<div className='priority-fields'>
					<span className='label'>{autopilotLabels.min}</span>
					<span className='label'>{autopilotLabels.max}</span>
				</div>
			</div>

			<div className='main-section'>
				<div className='priority-section'>
					<div className='row'>
						<span className='label large'>{autopilotLabels.high}</span>
						<div className='priority-fields'>
							<NumberInput
								name='priorityThresholdHigh'
								value={values.priorityThresholdHigh}
								onChange={handleChange}
								disabled={isReadOnly}
								styles={{ width: '120px' }}
							/>
							<div className='common-dropdown-container'>
								<OutlinedInput
									className='number-input'
									disabled
									style={{ width: '120px' }}
								/>
							</div>
						</div>
					</div>

					<div className='row'>
						<span className='label large'>{autopilotLabels.med}</span>
						<div className='priority-fields'>
							<NumberInput
								name='priorityThresholdMed'
								value={values.priorityThresholdMed}
								onChange={handleChange}
								disabled={isReadOnly}
								styles={{ width: '120px' }}
							/>
							<NumberInput
								name='priorityThresholdHigh'
								value={values.priorityThresholdHigh}
								onChange={handleChange}
								disabled={isReadOnly}
								styles={{ width: '120px' }}
							/>
						</div>
					</div>

					<div className='row'>
						<span className='label large'>{autopilotLabels.low}</span>
						<div className='priority-fields'>
							<NumberInput
								value={values.priorityThresholdLow}
								disabled
								styles={{ width: '120px' }}
							/>
							<NumberInput
								name='priorityThresholdMed'
								value={values.priorityThresholdMed}
								onChange={handleChange}
								disabled={isReadOnly}
								styles={{ width: '120px' }}
							/>
						</div>
					</div>
				</div>

				<div className='divider'>
					<hr />
				</div>

				<div className='autopilot-section'>
					<div className='row'>
						<div className='autopilot-row'>
							<AutopilotSelect name='autopilotHigh' />
							<IntradayAutopilotCheckbox
								name='includeIntradayHigh'
								regularAutopilot={values.autopilotHigh}
								includeIntraday={values.includeIntradayHigh}
							/>
						</div>
					</div>
					<div className='row'>
						<div className='autopilot-row'>
							<AutopilotSelect name='autopilotMed' />
							<IntradayAutopilotCheckbox
								name='includeIntradayMed'
								regularAutopilot={values.autopilotMed}
								includeIntraday={values.includeIntradayMed}
							/>
						</div>
					</div>
					<div className='row'>
						<div className='autopilot-row'>
							<AutopilotSelect name='autopilotLow' />
							<IntradayAutopilotCheckbox
								name='includeIntradayLow'
								regularAutopilot={values.autopilotLow}
								includeIntraday={values.includeIntradayLow}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
